import React, { useEffect, useRef, useState } from 'react';
import { useActiveAccount } from '@/hooks/useActiveAccount';
import { ChevronDown, Check, Phone } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

type Props = {
  /**
   * 'sidebar' — compact, with an "Active WhatsApp" caption (legacy placement).
   * 'panel'   — larger card for the top of the Chats list, where the number
   *             being viewed matters enough to sit next to the conversations.
   */
  variant?: 'sidebar' | 'panel';
};

function AccountAvatar({ url, size }: { url?: string; size: number }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [url]);
  const temporaryMetaUrl = /(?:fbcdn\.net|fbsbx\.com|whatsapp\.net|graph\.facebook\.com)/i.test(url || '');
  return url && !temporaryMetaUrl && !failed
    ? <img src={url} alt="" className="h-full w-full rounded-full object-cover" onError={() => setFailed(true)} />
    : <Phone size={size} />;
}

export const ActiveAccountPicker: React.FC<Props> = ({ variant = 'sidebar' }) => {
  const { loading, accounts, active, switchTo } = useActiveAccount();
  const [open, setOpen] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (loading || accounts.length === 0) return null;

  const onPick = async (uid: string) => {
    if (uid === active?.accountUid) { setOpen(false); return; }
    setBusyUid(uid);
    try {
      await switchTo(uid);
      const next = accounts.find((a) => a.accountUid === uid);
      toast.success(`Switched to ${next?.businessName ?? 'account'}`);
      setOpen(false);
    } catch {
      // axios interceptor already toasted backend message
    } finally {
      setBusyUid(null);
    }
  };

  const panel = variant === 'panel';

  return (
    <div className={clsx('relative', panel ? '' : 'px-3 pb-3')} ref={ref}>
      {!panel && (
        <div className="mb-1.5 px-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-text-muted">
          Active WhatsApp
        </div>
      )}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={clsx(
          'group flex w-full items-center gap-2 border border-border bg-surface-alt text-left transition-colors hover:bg-surface',
          panel ? 'rounded-xl px-3 py-2.5' : 'rounded-lg px-2.5 py-2',
        )}
      >
        <div className={clsx(
          'flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-success-soft text-success',
          panel ? 'h-9 w-9' : 'h-7 w-7',
        )}>
          <AccountAvatar url={active?.profilePicUrl} size={panel ? 16 : 13} />
        </div>
        <div className="min-w-0 flex-1">
          <div className={clsx('truncate font-semibold text-text-primary', panel ? 'text-sm' : 'text-[12.5px]')}>
            {active?.businessName ?? 'No default'}
          </div>
          <div className="flex items-center gap-1 truncate text-2xs text-text-muted">
            <Phone size={9} className="shrink-0" />
            {active?.displayPhoneNumber ?? '—'}
          </div>
        </div>
        <ChevronDown size={panel ? 15 : 13} className={clsx('shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className={clsx(
          'z-40 mt-1 max-h-72 overflow-y-auto rounded-xl border border-border bg-surface shadow-elevated',
          panel ? 'absolute left-0 right-0' : 'absolute left-3 right-3',
        )}>
          {accounts.map((acc) => {
            const isActive = active?.accountUid === acc.accountUid;
            const busy = busyUid === acc.accountUid;
            return (
              <button
                key={acc.accountUid}
                onClick={() => onPick(acc.accountUid)}
                disabled={busy}
                className={clsx(
                  'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
                  isActive ? 'bg-primary-soft' : 'hover:bg-surface-alt',
                  !acc.isActive && 'opacity-60',
                  busy && 'cursor-wait opacity-70',
                )}
              >
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-success-soft text-success">
                  <AccountAvatar url={acc.profilePicUrl} size={13} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12.5px] font-medium text-text-primary">{acc.businessName}</div>
                  <div className="truncate text-2xs text-text-muted">{acc.displayPhoneNumber}</div>
                </div>
                {isActive && <Check size={14} className="shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
